/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PermissionProvider } from './context/PermissionContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Templates from './pages/Templates';
import NewAssignment from './pages/NewAssignment';
import BulkSendAssignments from './pages/BulkSendAssignments';
import ConfirmBulkImport from './pages/ConfirmBulkImport';
import EditAssignment from './pages/EditAssignment';
import Dashboard from './pages/Dashboard';
import ReviewMedia from './pages/ReviewMedia';
import Capture from './pages/Capture';
import CaptureRedirect from './pages/CaptureRedirect';
import Settings from './pages/Settings';
import Layout from './components/Layout';
import Assignments from './pages/Assignments';
import ImportHistory from './pages/ImportHistory';
import Help from './pages/Help';
import Webhooks from './pages/Webhooks';
import ErrorPage from './pages/ErrorPage';
import WorkflowCreate from './pages/WorkflowCreate';
import WorkflowEdit from './pages/WorkflowEdit';
import Users from './pages/Users';
import Roles from './pages/Roles';
import WorkflowView from './pages/WorkflowView';
import Accounts from './pages/Accounts';
import NewAccount from './pages/NewAccount';
import EditAccount from './pages/EditAccount';
import SubAccounts from './pages/SubAccounts';
import NewSubAccount from './pages/NewSubAccount';
import CloneSubAccount from './pages/CloneSubAccount';
import EditSubAccount from './pages/EditSubAccount';
import NewUser from './pages/NewUser';
import NewRole from './pages/NewRole';
import EditUser from './pages/EditUser';
import EditRole from './pages/EditRole';
import Profile from './pages/Profile';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Redirects to `redirectTo` and opens the account switcher if no sub-account is selected
const SubAccountRoute = ({ children, redirectTo = '/dashboard' }) => {
  const { user } = useAuth();
  const needsSubAccount = !user?.subAccountUID;

  useEffect(() => {
    if (needsSubAccount) {
      window.dispatchEvent(new CustomEvent('sub-account-required'));
    }
  }, [needsSubAccount]);

  if (needsSubAccount) {
    return <Navigate to={redirectTo} replace />;
  }
  return children;
};

SubAccountRoute.propTypes = {
  children: PropTypes.node.isRequired,
  redirectTo: PropTypes.string,
};

const AppRoutes = () => {
  const { user, loading } = useAuth();

  if (loading) { return null; }

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/dashboard" replace /> : <ForgotPassword />} />
      <Route path="/reset-password" element={user ? <Navigate to="/dashboard" replace /> : <ResetPassword />} />
      <Route path="/capture/:token" element={<Capture />} />
      <Route path="/c/:shortToken" element={<CaptureRedirect />} />
      <Route path="/error" element={<ErrorPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />

        {/* Workflow/Template Management */}
        <Route path="workflows" element={<ProtectedRoute permissions={['templates.view']}><Templates /></ProtectedRoute>} />
        <Route path="workflow/new" element={<SubAccountRoute redirectTo="/workflows"><ProtectedRoute permissions={['templates.create']}><WorkflowCreate /></ProtectedRoute></SubAccountRoute>} />
        <Route path="workflow/:id" element={<ProtectedRoute permissions={['templates.view']}><WorkflowView /></ProtectedRoute>} />
        <Route path="workflow/:id/edit" element={<SubAccountRoute redirectTo="/workflows"><ProtectedRoute permissions={['templates.update']}><WorkflowEdit /></ProtectedRoute></SubAccountRoute>} />

        {/* Assignment Management */}
        <Route path="assignments" element={<ProtectedRoute permissions={['assignments.view']}><Assignments /></ProtectedRoute>} />
        <Route path="import-history" element={<ProtectedRoute permissions={['assignments.view']}><ImportHistory /></ProtectedRoute>} />
        <Route path="assignments/new" element={<SubAccountRoute redirectTo="/assignments"><ProtectedRoute permissions={['assignments.create']}><NewAssignment /></ProtectedRoute></SubAccountRoute>} />
        <Route path="assignments/bulk-send" element={<SubAccountRoute redirectTo="/assignments"><ProtectedRoute permissions={['assignments.create']}><BulkSendAssignments /></ProtectedRoute></SubAccountRoute>} />
        <Route path="assignments/confirm-import/:importId" element={<SubAccountRoute redirectTo="/assignments"><ProtectedRoute permissions={['assignments.create']}><ConfirmBulkImport /></ProtectedRoute></SubAccountRoute>} />
        <Route path="assignments/:id" element={<ProtectedRoute permissions={['assignments.view_media']}><ReviewMedia /></ProtectedRoute>} />
        <Route path="assignments/:id/edit" element={<SubAccountRoute redirectTo="/assignments"><ProtectedRoute permissions={['assignments.update']}><EditAssignment /></ProtectedRoute></SubAccountRoute>} />

        {/* User Management */}
        <Route path="users" element={<ProtectedRoute permissions={['users.view']}><Users /></ProtectedRoute>} />
        <Route path="users/new" element={<ProtectedRoute permissions={['users.create']}><NewUser /></ProtectedRoute>} />
        <Route path="users/:id/edit" element={<ProtectedRoute permissions={['users.update']}><EditUser /></ProtectedRoute>} />

        {/* Role Management */}
        <Route path="roles" element={<ProtectedRoute permissions={['roles.view']}><Roles /></ProtectedRoute>} />
        <Route path="roles/new" element={<ProtectedRoute permissions={['roles.create']}><NewRole /></ProtectedRoute>} />
        <Route path="roles/:id/edit" element={<ProtectedRoute permissions={['roles.update']}><EditRole /></ProtectedRoute>} />

        {/* Account Management */}
        <Route path="accounts" element={<ProtectedRoute permissions={['accounts.view']}><Accounts /></ProtectedRoute>} />
        <Route path="accounts/new" element={<ProtectedRoute permissions={['accounts.create']}><NewAccount /></ProtectedRoute>} />
        <Route path="accounts/:id/edit" element={<ProtectedRoute permissions={['accounts.update']}><EditAccount /></ProtectedRoute>} />

        {/* Sub Account Management */}
        <Route path="sub-accounts" element={<ProtectedRoute permissions={['sub_accounts.view']}><SubAccounts /></ProtectedRoute>} />
        <Route path="sub-accounts/new" element={<ProtectedRoute permissions={['sub_accounts.create']}><NewSubAccount /></ProtectedRoute>} />
        <Route path="sub-accounts/:id/clone" element={<ProtectedRoute permissions={['sub_accounts.create']}><CloneSubAccount /></ProtectedRoute>} />
        <Route path="sub-accounts/:id/edit" element={<ProtectedRoute permissions={['sub_accounts.update']}><EditSubAccount /></ProtectedRoute>} />

        <Route path="webhooks" element={<ProtectedRoute permissions={['webhooks.view']}><Webhooks /></ProtectedRoute>} />
        <Route path="settings" element={<ProtectedRoute permissions={['settings.manage']}><Settings /></ProtectedRoute>} />
        <Route path="help" element={<Help />} />
        <Route path="profile" element={<Profile />} />

      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

const App = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PermissionProvider>
          <AppRoutes />
        </PermissionProvider>
        <ToastContainer />
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
